import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import Header from "@/components/layout/header";
import { 
  Receipt, Download, DollarSign, AlertCircle, CheckCircle, 
  Clock, Search, Filter, MoreVertical, Eye, Send, ChevronDown,
  X, Plus, Minus, Calendar, Building2, CreditCard, FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import type { FinancialTransaction, Tenant, CorporateClient } from "@/types/shared";

interface BillingRecord {
  id: number;
  tenantId: number;
  tenantName: string;
  tenantLogo?: string;
  clientName: string;
  planTier: string;
  billingPeriod: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'overdue';
  invoiceNumber: string;
  dueDate: string;
  transactions: FinancialTransaction[];
}

interface FinancialStats {
  totalBilled: number;
  outstandingBalance: number;
  paidInvoices: number;
  overdueInvoices: number;
}

export default function FinancialOperations() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<BillingRecord | null>(null);
  const { toast } = useToast();

  // API calls
  const { data: billingRecords = [], isLoading: recordsLoading } = useQuery<BillingRecord[]>({
    queryKey: ['/api/financial/billing'],
  });

  const { data: stats = {
    totalBilled: 0,
    outstandingBalance: 0,
    paidInvoices: 0,
    overdueInvoices: 0
  }, isLoading: statsLoading } = useQuery<FinancialStats>({
    queryKey: ['/api/financial/stats'],
  });

  // Filter billing records
  const filteredRecords = billingRecords.filter(record => {
    const matchesSearch = record.tenantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
    const matchesPlan = planFilter === 'all' || record.planTier === planFilter;
    
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Handlers
  const handleSelectAll = () => {
    if (selectedRecords.length === filteredRecords.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecords.map(r => r.id));
    }
  };

  const handleSelectRecord = (id: number) => {
    setSelectedRecords(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const handleViewInvoice = (record: BillingRecord) => {
    setSelectedInvoice(record);
    setInvoiceDrawerOpen(true);
  };

  const handleExportTransactions = () => {
    const csvContent = [
      ['Invoice #', 'Tenant', 'Client', 'Plan', 'Period', 'Amount (AED)', 'Status', 'Due Date'],
      ...filteredRecords.map(r => [
        r.invoiceNumber,
        r.tenantName,
        r.clientName,
        r.planTier,
        r.billingPeriod,
        r.amount.toString(),
        r.status,
        r.dueDate
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkAction = (action: string) => {
    switch (action) {
      case 'generate':
        toast({
          title: "Generating invoices",
          description: `Creating invoices for ${selectedRecords.length} tenant(s)...`,
        });
        break;
      case 'reminder':
        toast({
          title: "Sending reminders",
          description: `Sending payment reminders to ${selectedRecords.length} tenant(s)...`,
        });
        break;
      case 'export':
        handleExportTransactions();
        break;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'paid':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'overdue':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-3 h-3" />;
      case 'pending':
        return <Clock className="w-3 h-3" />;
      case 'overdue':
        return <AlertCircle className="w-3 h-3" />;
      default:
        return null;
    }
  };

  if (statsLoading) {
    return (
      <>
        <Header 
          title="Financial Operations" 
          description="View and manage tenant billing, usage, and invoices"
        />
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header 
        title="Financial Operations" 
        description="View and manage tenant billing, usage, and invoices"
      >
        <Button className="athlyn-button-primary">
          <Receipt className="w-4 h-4 mr-2" />
          Generate Invoice
        </Button>
        <Button variant="outline" onClick={handleExportTransactions}>
          <Download className="w-4 h-4 mr-2" />
          Export Transactions
        </Button>
      </Header>
      
      <div className="flex-1 overflow-auto p-6">
        {/* Summary KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Billed (AED)</p>
                  <p className="text-2xl font-semibold">{stats.totalBilled.toLocaleString()}</p>
                </div>
                <DollarSign className="w-8 h-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Outstanding Balance</p>
                  <p className="text-2xl font-semibold text-orange-600">{stats.outstandingBalance.toLocaleString()}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Paid Invoices</p>
                  <p className="text-2xl font-semibold text-green-600">{stats.paidInvoices}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="athlyn-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Overdue Invoices</p>
                  <p className="text-2xl font-semibold text-red-600">{stats.overdueInvoices}</p>
                </div>
                <Clock className="w-8 h-8 text-red-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="athlyn-card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 athlyn-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Plan Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="Starter">Starter</SelectItem>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedRecords.length > 0 && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4 flex items-center justify-between">
            <span className="text-sm font-medium">
              {selectedRecords.length} record(s) selected
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline">
                  Bulk Actions
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleBulkAction('generate')}>
                  <Receipt className="w-4 h-4 mr-2" />
                  Generate Invoices
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction('reminder')}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Reminders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction('export')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Billing Table */}
        {recordsLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : filteredRecords.length === 0 ? (
          <Card className="athlyn-card">
            <CardContent className="p-12 text-center">
              <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No billing records</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Try adjusting your search filters' : 'Generate your first invoice'}
              </p>
              <Button className="athlyn-button-primary">
                Generate Invoice
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="athlyn-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="athlyn-table-header">
                      <Checkbox
                        checked={selectedRecords.length === filteredRecords.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="athlyn-table-header text-left">Tenant</th>
                    <th className="athlyn-table-header text-left">Plan Tier</th>
                    <th className="athlyn-table-header text-left">Billing Period</th>
                    <th className="athlyn-table-header text-left">Amount (AED)</th>
                    <th className="athlyn-table-header text-left">Status</th>
                    <th className="athlyn-table-header text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id} className="border-b hover:bg-muted/50">
                      <td className="athlyn-table-cell">
                        <Checkbox
                          checked={selectedRecords.includes(record.id)}
                          onCheckedChange={() => handleSelectRecord(record.id)}
                        />
                      </td>
                      <td className="athlyn-table-cell">
                        <div className="flex items-center gap-3">
                          <img 
                            src={record.tenantLogo} 
                            alt={record.tenantName}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                          <div>
                            <p className="font-medium">{record.tenantName}</p>
                            <p className="text-sm text-muted-foreground">{record.clientName}</p>
                          </div>
                        </div>
                      </td>
                      <td className="athlyn-table-cell">
                        <Badge variant="outline">{record.planTier}</Badge>
                      </td>
                      <td className="athlyn-table-cell">{record.billingPeriod}</td>
                      <td className="athlyn-table-cell font-medium">
                        {record.amount.toLocaleString()}
                      </td>
                      <td className="athlyn-table-cell">
                        <Badge variant={getStatusVariant(record.status)}>
                          {getStatusIcon(record.status)}
                          <span className="ml-1">{record.status}</span>
                        </Badge>
                      </td>
                      <td className="athlyn-table-cell">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewInvoice(record)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Invoice Detail Drawer */}
      <Sheet open={invoiceDrawerOpen} onOpenChange={setInvoiceDrawerOpen}>
        <SheetContent className="w-full md:w-[600px] flex flex-col">
          <SheetHeader>
            <SheetTitle>Invoice Details</SheetTitle>
            <SheetDescription>
              {selectedInvoice?.invoiceNumber} • {selectedInvoice?.tenantName}
            </SheetDescription>
          </SheetHeader>
          
          {selectedInvoice && (
            <div className="flex-1 overflow-auto">
              <Tabs defaultValue="summary" className="mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="summary">Summary</TabsTrigger>
                  <TabsTrigger value="history">Payment History</TabsTrigger>
                  <TabsTrigger value="adjustments">Adjustments</TabsTrigger>
                </TabsList>
                
                <TabsContent value="summary" className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Billing Period</span>
                      <span className="font-medium">{selectedInvoice.billingPeriod}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Plan Tier</span>
                      <Badge variant="outline">{selectedInvoice.planTier}</Badge>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Due Date</span>
                      <span className="font-medium">{selectedInvoice.dueDate}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant={getStatusVariant(selectedInvoice.status)}>
                        {getStatusIcon(selectedInvoice.status)}
                        <span className="ml-1">{selectedInvoice.status}</span>
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Line Items</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm">Subscription Fee</span>
                        <span className="font-medium">AED {selectedInvoice.amount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm">Usage Overages</span>
                        <span className="font-medium">AED 0</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm">One-time Charges</span>
                        <span className="font-medium">AED 0</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Amount</span>
                      <span className="text-xl font-bold text-primary">
                        AED {selectedInvoice.amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="history" className="space-y-4">
                  <div className="text-center py-8 text-muted-foreground">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No payment history available</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="adjustments" className="space-y-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Credit/Debit Memos</h4>
                      <Button size="sm" variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Adjustment
                      </Button>
                    </div>
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No adjustments applied</p>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          <SheetFooter className="mt-6">
            <Button 
              className="athlyn-button-primary"
              disabled={selectedInvoice?.status === 'paid'}
            >
              Mark as Paid
            </Button>
            <Button variant="outline">
              <Send className="w-4 h-4 mr-2" />
              Send Reminder
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}